/** « Tester l'IA » (C1/C4): chat with the agent like a client; nothing is sent to anyone.
 * Design v2.1: a conversation window like Messages (the tester writes as the client, on the right;
 * the AI answers on the left with its outcome and the bible version it used), a typing indicator,
 * suggestions to start, and a composer docked at the bottom. */

import { type SandboxRequestLanguageEnum, useAssistantSandboxCreate } from "@agencyos/api-client";
import { Avatar, Badge, Button, SegmentedControl, TextAreaField } from "@agencyos/ui-web";
import { FlaskConical, RotateCcw, Sparkles } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Page } from "../components/AppShell";

interface Turn {
  role: "user" | "model";
  text: string;
  outcome?: string;
  reason?: string;
  bible?: number | null;
  quote?: Record<string, string>;
}

const SUGGESTIONS = ["price", "installments", "documents"] as const;

export function AiTestPage() {
  const { t } = useTranslation();
  const sandbox = useAssistantSandboxCreate();
  const [language, setLanguage] = useState<SandboxRequestLanguageEnum>("fr");
  const [turns, setTurns] = useState<Turn[]>([]);
  const [text, setText] = useState("");
  const scroller = useRef<HTMLOListElement>(null);

  useEffect(() => {
    const list = scroller.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [turns.length, sandbox.isPending]);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!text.trim()) return;
    const history: Turn[] = [...turns, { role: "user", text: text.trim() }];
    setTurns(history);
    setText("");
    sandbox.mutate(
      { data: { language, history: history.map(({ role, text: body }) => ({ role, text: body })) } },
      {
        onSuccess: (response) => {
          if (response.status !== 200) return;
          const r = response.data;
          setTurns([
            ...history,
            { role: "model", text: r.reply, outcome: r.outcome, reason: r.reason, bible: r.bible_version, quote: r.flight_quote },
          ]);
        },
      },
    );
  };

  return (
    <Page
      title={t("aiTest.title")}
      description={t("aiTest.intro")}
      actions={
        <SegmentedControl<SandboxRequestLanguageEnum>
          label={t("aiTest.language")}
          value={language}
          options={(["fr", "en", "pcm"] as const).map((l) => ({ value: l, label: t(`aiTest.languages.${l}`) }))}
          onChange={setLanguage}
        />
      }
    >
      <section className="ai-chat ao-glass" aria-label={t("aiTest.chat")}>
        <header className="ai-chat__head">
          <span className="ai-chat__avatar" aria-hidden="true">
            <Sparkles size={18} strokeWidth={1.9} />
          </span>
          <div className="ai-chat__who">
            <h2>{t("aiTest.agent")}</h2>
            <p className="muted">{t("aiTest.agentHint")}</p>
          </div>
          <Badge tone="offer">
            <FlaskConical size={12} strokeWidth={2} aria-hidden="true" /> {t("aiTest.outcome.sandbox")}
          </Badge>
          <Button variant="plain" size="sm" icon={<RotateCcw size={15} strokeWidth={1.9} aria-hidden="true" />} onClick={() => setTurns([])}>
            {t("aiTest.reset")}
          </Button>
        </header>
        <ol className="messages ai-chat__messages" aria-live="polite" ref={scroller}>
          {turns.length === 0 ? (
            <li className="ai-chat__empty">
              <p className="muted">{t("aiTest.startHint")}</p>
              <div className="ai-chat__suggestions">
                {SUGGESTIONS.map((key) => (
                  <button key={key} type="button" className="ai-chat__suggestion" onClick={() => setText(t(`aiTest.suggestions.${key}`))}>
                    {t(`aiTest.suggestions.${key}`)}
                  </button>
                ))}
              </div>
            </li>
          ) : null}
          {turns.map((turn, index) => (
            <li key={index} className={`bubble bubble--${turn.role === "user" ? "out" : "in"}`}>
              <p className="bubble__body">{turn.text}</p>
              {turn.role === "model" ? (
                <p className="bubble__meta ai-chat__meta">
                  <Badge tone={turn.outcome === "replied" ? "success" : "danger"}>{t(`aiTest.outcome.${turn.outcome ?? "sandbox"}`)}</Badge>
                  <span>{turn.bible ? t("aiTest.bible", { version: turn.bible }) : t("aiTest.noBible")}</span>
                  {turn.reason ? <span>{t("aiTest.reason", { reason: turn.reason })}</span> : null}
                  {turn.quote && Object.keys(turn.quote).length ? (
                    <span>{t("aiTest.quote", { trip: Object.values(turn.quote).join(" · ") })}</span>
                  ) : null}
                </p>
              ) : null}
            </li>
          ))}
          {sandbox.isPending ? (
            <li className="bubble bubble--in ai-chat__typing" role="status" aria-label={t("common.loading")}>
              <span />
              <span />
              <span />
            </li>
          ) : null}
        </ol>
        <form onSubmit={onSubmit} className="inbox__composer ai-chat__composer">
          <div className="ai-chat__client">
            <Avatar name={t("aiTest.clientName")} />
            <span className="muted">{t("aiTest.asClient")}</span>
          </div>
          <TextAreaField label={t("aiTest.message")} rows={2} value={text} onChange={(e) => setText(e.target.value)} />
          <div className="inbox__composer-actions">
            <Button type="submit" disabled={sandbox.isPending || !text.trim()}>
              {sandbox.isPending ? t("common.loading") : t("aiTest.send")}
            </Button>
          </div>
        </form>
      </section>
    </Page>
  );
}
